/**
 * Form.io validation UI helpers — same contract as user portal:
 * red underline + message only on empty required fields (not filled / optional).
 */

const INVALID_UNDERLINE = "1.5px solid #c0392b";

/** Layout / non-input types — never paint these (would red-line every child). */
const NON_FIELD_TYPES = new Set([
  "button",
  "hidden",
  "htmlelement",
  "content",
  "panel",
  "columns",
  "table",
  "well",
  "fieldset",
  "container",
  "datagrid",
  "editgrid",
  "tabs",
  "form",
  "datasource",
]);

/** Red bottom underline — same look as user portal. */
function applyInvalidUnderline(el) {
  if (!el || el.nodeType !== 1) return;
  el.classList.add("is-invalid");
  el.style.setProperty("border", "none", "important");
  el.style.setProperty("border-bottom", INVALID_UNDERLINE, "important");
  el.style.setProperty("border-bottom-color", "#c0392b", "important");
  el.style.setProperty("box-shadow", "none", "important");
  el.style.setProperty("background-image", "none", "important");
}

function clearInvalidUnderline(el) {
  if (!el || el.nodeType !== 1) return;
  el.classList.remove("is-invalid");
  el.style.removeProperty("border");
  el.style.removeProperty("border-bottom");
  el.style.removeProperty("border-bottom-color");
  el.style.removeProperty("box-shadow");
  el.style.removeProperty("background-image");
}

function isFieldComponent(comp) {
  const type = comp?.component?.type;
  if (!type || NON_FIELD_TYPES.has(type)) return false;
  if (comp.component?.input === false && type !== "selectboxes") return false;
  return true;
}

function isComponentEmpty(comp) {
  const val =
    typeof comp.getValue === "function" ? comp.getValue() : comp.dataValue;
  if (val == null || val === "") return true;
  if (Array.isArray(val)) return val.length === 0;
  if (typeof val === "object") {
    // datetime / day objects — treat as filled if any meaningful key set
    if (val instanceof Date) return Number.isNaN(val.getTime());
    const keys = Object.keys(val);
    if (keys.length === 0) return true;
    if ("date" in val || "time" in val || "year" in val || "month" in val || "day" in val) {
      return !keys.some((k) => {
        const v = val[k];
        return v != null && v !== "";
      });
    }
    // select-like object with value/label
    if ("value" in val || "label" in val) {
      return !(val.value || val.label);
    }
  }
  return false;
}

function isRequiredComponent(comp) {
  return !!(comp?.component?.validate?.required || comp?.validators?.required);
}

/** Only paint controls that belong to this field — never nested sibling fields. */
function eachOwnControl(root, fn) {
  if (!root?.querySelectorAll) return;
  const nodes = root.querySelectorAll(
    ".choices, .choices__inner, input:not([type='hidden']):not([type='radio']):not([type='checkbox']), select, textarea, .form-control, .form-select"
  );
  nodes.forEach((el) => {
    const owner = el.closest(".formio-component");
    if (owner !== root) return;
    fn(el);
  });
}

/**
 * Force Form.io to mark invalid fields (red underline + error text).
 * Only empty required leaf fields — filled / optional stay clean.
 * @returns {boolean} true if form valid
 */
/** Prevents force → showErrors → onError → force recursion (datetime silentCheck quirk). */
let forceValidationReentry = false;

export function forceFormioShowValidationErrors(formio) {
  if (!formio) return true;
  if (forceValidationReentry) return !formio.errors?.length;
  forceValidationReentry = true;

  try {
    // Wipe previous paint so filled fields never keep stale red lines.
    clearFormioValidationState(formio);

    try {
      formio.submitted = true;
      formio.pristine = false;
    } catch {
      /* ignore */
    }

    let valid = true;
    if (typeof formio.checkValidity === "function") {
      try {
        valid = !!formio.checkValidity(formio.data, true, formio.data, true);
      } catch {
        valid = false;
      }
    }

    if (!valid && typeof formio.showErrors === "function") {
      try {
        const errs = typeof formio.errors !== "undefined" ? formio.errors : true;
        // triggerEvent=false: DateTime ignores silentCheck and fills formio.errors;
        // true would emit onError → caller force again → infinite loop (Travel hang).
        formio.showErrors(errs, false);
      } catch {
        /* ignore */
      }
    }

  const markComponent = (comp, message) => {
    if (!comp || !isFieldComponent(comp)) return;
    if (comp.visible === false) return;
    // Same as user portal: only empty required fields get red underline.
    if (!isRequiredComponent(comp) || !isComponentEmpty(comp)) return;

    valid = false;
    const msg =
      message ||
      comp.error?.message ||
      (Array.isArray(comp.errors) && comp.errors[0]?.message) ||
      (comp.component?.label
        ? `${comp.component.label} is required`
        : "This field is required");

    try {
      if (typeof comp.setCustomValidity === "function") {
        comp.setCustomValidity(msg, true);
      }
    } catch {
      /* ignore */
    }

    const root = comp.element;
    if (!root) return;

    root.classList.add("has-error", "formio-error");
    root.setAttribute("data-participant-invalid", "1");

    /* Expand collapsed panel ancestors so field is visible (no redraw — keeps marks). */
    try {
      let parent = comp.parent;
      while (parent) {
        if (parent.component?.type === "panel" && parent.collapsed) {
          parent.collapsed = false;
          const header = parent.element?.querySelector?.(
            ".card-header, .panel-heading, [data-toggle='collapse']"
          );
          const body = parent.element?.querySelector?.(
            ".card-body, .panel-body, .collapse"
          );
          if (body) {
            body.classList.add("show");
            body.style.display = "block";
          }
          if (header) header.classList.remove("collapsed");
        }
        parent = parent.parent;
      }
    } catch {
      /* ignore */
    }

    eachOwnControl(root, (el) => {
      if (el.classList?.contains("choices")) {
        el.classList.add("is-invalid");
        return;
      }
      if (el.classList?.contains("choices__inner") || el.matches?.("input, select, textarea, .form-control, .form-select")) {
        applyInvalidUnderline(el);
      }
    });

    let errEl = root.querySelector(":scope > .formio-errors, :scope > .form-group > .formio-errors, :scope > .field-content > .formio-errors");
    if (!errEl) {
      errEl = document.createElement("div");
      errEl.className = "formio-errors";
      errEl.dataset.participantInjected = "1";
      const mount =
        root.querySelector(":scope > .field-content") ||
        root.querySelector(":scope > .form-group") ||
        root;
      mount.appendChild(errEl);
    }
    const safe = String(msg)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    errEl.innerHTML = `<div class="formio-error formio-error-message error">${safe}</div>`;
    errEl.style.setProperty("display", "block", "important");
    errEl.style.setProperty("visibility", "visible", "important");
    errEl.style.setProperty("color", "#c0392b", "important");
    errEl.style.setProperty("font-size", "12px", "important");
    errEl.style.setProperty("margin-top", "6px", "important");
  };

  // Mark every empty required leaf field (authoritative — avoids parent/column bleed).
  if (typeof formio.everyComponent === "function") {
    formio.everyComponent((comp) => {
      if (!isFieldComponent(comp)) return;
      if (comp.visible === false) return;
      if (!isRequiredComponent(comp)) return;
      if (!isComponentEmpty(comp)) return;
      markComponent(comp);
    });
  }

    return valid;
  } finally {
    forceValidationReentry = false;
  }
}

/**
 * Collect validation messages for the top summary list (participant edit).
 * @returns {{ text: string, fieldKey?: string }[]}
 */
export function collectFormioValidationItems(formio, err) {
  if (formio && typeof formio.checkValidity === "function") {
    try {
      formio.checkValidity(formio.data, true, formio.data, true);
    } catch {
      /* ignore */
    }
  }

  const seen = new Set();
  const items = [];

  const push = (msg, fieldKey) => {
    if (!msg) return;
    const id = fieldKey || msg;
    if (seen.has(id)) return;
    seen.add(id);
    items.push({ text: msg, fieldKey: fieldKey || undefined });
  };

  if (formio && typeof formio.everyComponent === "function") {
    formio.everyComponent((comp) => {
      if (!isFieldComponent(comp)) return;
      if (!isRequiredComponent(comp)) return;
      if (!isComponentEmpty(comp)) return;
      const msg =
        comp.error?.message ||
        (comp.component?.label
          ? `${comp.component.label} is required`
          : "This field is required");
      push(msg, comp.component?.key || comp.key);
    });
  }

  if (items.length === 0 && err) {
    const raw = Array.isArray(err)
      ? err
      : err?.errors || err?.messages || (err?.message ? [err] : []);
    const list = Array.isArray(raw) ? raw : [raw];
    list.forEach((e) => {
      const msg =
        e?.message ||
        e?.msg ||
        (typeof e === "string" ? e : null) ||
        (e?.component?.label ? `${e.component.label} is required` : null);
      const fieldKey = e?.component?.key || e?.key || e?.field || undefined;
      push(msg, fieldKey);
    });
  }

  return items;
}

/** Clear Form.io validation UI after a successful save / before re-paint. */
export function clearFormioValidationState(formio) {
  if (!formio) return;

  if (typeof formio.setPristine === "function") {
    try {
      formio.setPristine(true);
    } catch {
      /* ignore */
    }
  }

  try {
    formio.submitted = false;
  } catch {
    /* ignore */
  }

  if (typeof formio.everyComponent === "function") {
    formio.everyComponent((comp) => {
      if (!comp) return;
      try {
        if (typeof comp.setCustomValidity === "function") comp.setCustomValidity("");
        if (typeof comp.setPristine === "function") comp.setPristine(true);
        comp.error = null;
        if (Array.isArray(comp.errors)) comp.errors = [];
      } catch {
        /* ignore */
      }
      const root = comp.element;
      if (!root?.querySelectorAll) return;
      root.classList.remove("has-error", "formio-error");
      root.removeAttribute("data-participant-invalid");
      eachOwnControl(root, (el) => {
        el.classList.remove("is-invalid");
        clearInvalidUnderline(el);
      });
      root.querySelectorAll(".formio-errors").forEach((el) => {
        el.style.removeProperty("display");
        el.style.removeProperty("visibility");
        el.style.removeProperty("color");
        if (el.dataset?.participantInjected === "1") {
          el.remove();
        } else {
          el.innerHTML = "";
        }
      });
    });
  }

  const root = formio.element || formio.form?.element;
  if (root?.querySelectorAll) {
    root.querySelectorAll(".alert-danger, .formio-error-wrapper, .formio-alert").forEach((el) => {
      el.remove();
    });
  }
}
